import React, { FC } from 'react';
import { useFormikContext } from 'formik';
import { Pill } from '@clearcover/atlas';
import { Column, Row } from 'Shared/components';
import { combineStrings, isBlank } from 'Shared/utils';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import { GetDetailsResponseType } from 'QuoteApp/services/endpoints/GetDetails';
import { Accordion, AccordionHeader, AccordionItem } from '../Accordion';
import { CoverageInput } from './CoverageInput';
import styles from './styles.module.css';
import { StackedCoverageMap } from 'QuoteApp/utils';

type CoveragesListPropsType = {
  config: GetConfigResponseType['coverages'];
  details: GetDetailsResponseType;
  onCoveragesPage?: boolean;
  stackedCoverages?: StackedCoverageMap;
};

export type CoveragesValuesType = {
  coverages: {
    policy: Record<string, string>;
    vehicles: Record<string, Record<string, string>>;
  };
};

const findWaivedSignaturesRequired = (
  values: Record<string, string>,
  config:
    | GetConfigResponseType['coverages']['insurableItem']
    | GetConfigResponseType['coverages']['insuranceProduct']
) => {
  return Object.entries(values).some(([coverageKey, coverageValue]) => {
    if (['waived', 'confirmed'].includes(coverageValue)) {
      const coverageConfig = config.find((c) => c.coverageType === coverageKey);

      if (coverageValue === 'waived') {
        return coverageConfig?.signatureRequiredToWaive;
      }

      if (coverageValue === 'confirmed') {
        return coverageConfig?.signatureRequiredToInclude;
      }
    }

    return false;
  });
};

const CoverageHeader: FC = () => (
  <div className={styles.wrapper}>
    <div className={styles.coverageHeader}>
      <Row>
        <Column className={styles.labelColumn}>
          <div className={styles.headerItem}>Coverage</div>
        </Column>
        <Column className={styles.optionsColumn}>
          <div className={styles.headerItem}>Options</div>
        </Column>
      </Row>
    </div>
  </div>
);

export const CoveragesList: FC<CoveragesListPropsType> = ({
  config,
  details,
  onCoveragesPage,
  stackedCoverages,
}) => {
  const { values, status } = useFormikContext<CoveragesValuesType>();
  const vehicleIds = Object.keys(values.coverages.vehicles);
  const policyHasWaivedSignaturesRequired = findWaivedSignaturesRequired(
    values.coverages.policy,
    config.insuranceProduct
  );

  const policyCoveragesDataComplete = Object.entries(
    values.coverages.policy
  ).every(([key, value]) => {
    const coverageConfig = config.insuranceProduct.find(
      (c) => c.coverageType === key
    );

    return coverageConfig && value in coverageConfig.coverageLevels;
  });
  const policyCoveragesDataInvalid =
    policyCoveragesDataComplete && !isBlank(status?.errors?.coverages?.policy);

  // Create an array of all stacked coverages names
  const stackedCoveragesNames = Object.keys(stackedCoverages || {}).reduce<
    string[]
  >((acc, key) => {
    const stackedCoverage = stackedCoverages?.[key];
    return acc.concat(stackedCoverage?.map((c) => c) || []);
  }, []);

  const policyAccordionData = {
    id: 'policy-level-coverages',
    header: (
      <div className={styles.policyHeaderContainer}>
        <AccordionHeader
          entity="coverage"
          header="Policy Level"
          dataComplete={policyCoveragesDataComplete ? undefined : false}
          dataInvalid={policyCoveragesDataInvalid}
        />
        {policyHasWaivedSignaturesRequired && (
          <Pill variant="warn">Waiver Required</Pill>
        )}
      </div>
    ),
    startOpen: onCoveragesPage,
    collapsibleContent: (
      <div className={styles.policyCoverages}>
        <CoverageHeader />
        <div>
          {Object.keys(values.coverages.policy)
            // Filter out stacked coverages, to not render them in the policy level accordion, we only render the inputs
            .filter((key) => !stackedCoveragesNames.includes(key))
            .map((coverageKey) => {
              // TODO: this should never be null but if we don't get a coverage type for
              // a given application coverage, we should report it up to Datadog
              const coverageConfig = config.insuranceProduct.find(
                (c) => c.coverageType === coverageKey
              );

              // get the stacked coverages for the current coverage, if any
              const stacked = config.insuranceProduct.filter((c) =>
                (stackedCoverages?.[coverageKey] || []).some(
                  (sc: string) => sc === c.coverageType
                )
              );

              return (
                <CoverageInput
                  key={coverageKey}
                  coverageFormKey={`coverages.policy.${coverageKey}`}
                  coverageConfig={coverageConfig}
                  stackedCoverages={stacked}
                />
              );
            })}
        </div>
      </div>
    ),
  };

  const vehicleAccordionData = vehicleIds.map((vehicleId, i) => {
    const label = combineStrings([
      details.vehicles[vehicleId]?.year.value,
      details.vehicles[vehicleId]?.make.value,
      details.vehicles[vehicleId]?.model.value,
    ]);

    const vehicleDisplay = label === '' ? `Vehicle ${i + 1}` : label;
    const vehicleHasWaivedSignaturesRequired = findWaivedSignaturesRequired(
      values.coverages.vehicles[vehicleId],
      config.insurableItem
    );
    const vehicleCoveragesDataComplete = Object.entries(
      values.coverages.vehicles[vehicleId]
    ).every(([key, value]) => {
      const coverageConfig = config.insurableItem.find(
        (c) => c.coverageType === key
      );

      return coverageConfig && value in coverageConfig.coverageLevels;
    });
    const vehicleCoveragesDataInvalid =
      vehicleCoveragesDataComplete &&
      !isBlank(status?.errors?.coverages?.vehicles?.[vehicleId]);

    return {
      id: `${vehicleId}-coverages`,
      header: (
        <div className={styles.policyHeaderContainer}>
          <AccordionHeader
            entity="coverage"
            header={vehicleDisplay === '' ? `Vehicle ${i + 1}` : vehicleDisplay}
            dataComplete={vehicleCoveragesDataComplete ? undefined : false}
            dataInvalid={vehicleCoveragesDataInvalid}
          />
          {vehicleHasWaivedSignaturesRequired && (
            <Pill variant="warn">Waiver Required</Pill>
          )}
        </div>
      ),
      startOpen: onCoveragesPage || !vehicleIds.includes(vehicleId),
      collapsibleContent: (
        <div className={styles.vehicleCoverage}>
          <CoverageHeader />
          {Object.keys(values.coverages.vehicles[vehicleId]).map(
            (coverageKey) => {
              // TODO: this should never be null but if we don't get a coverage type for
              // a given application coverage, we should report it up to Datadog
              const coverageConfig = config.insurableItem.find(
                (c) => c.coverageType === coverageKey
              );

              return (
                <CoverageInput
                  key={coverageKey}
                  coverageFormKey={`coverages.vehicles.${vehicleId}.${coverageKey}`}
                  coverageConfig={coverageConfig}
                />
              );
            }
          )}
        </div>
      ),
    };
  });

  return (
    <div data-testid="coverages-list">
      <Accordion>
        {[policyAccordionData, ...vehicleAccordionData].map(
          (coverageSection) => {
            return (
              <AccordionItem
                key={coverageSection.id}
                startOpen={coverageSection.startOpen}
                identifier={coverageSection.id}
                header={coverageSection.header}
              >
                {coverageSection.collapsibleContent}
              </AccordionItem>
            );
          }
        )}
      </Accordion>
    </div>
  );
};
